/* eslint-disable import/no-cycle */
import React, { useState, useRef, useEffect } from 'react';
import { SpreadsheetComponent, SheetsDirective, SheetDirective, RangesDirective, RangeDirective, ColumnsDirective, ColumnDirective, RowsDirective, RowDirective, CellsDirective, CellDirective } from '@syncfusion/ej2-react-spreadsheet';
// getRangeAddress
import { DialogUtility } from '@syncfusion/ej2-react-popups';
import { Header } from '.';

const MONTHS = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

const REQ_ROWS = 6;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const asText = (v) => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v).trim();
};

const asNumber = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''));
  return Number.isNaN(n) ? 0 : n;
};

const flagYes = (v) => /^(y|yes|true|1|x)$/i.test(asText(v));

const isOnDuty = (status) => {
  const s = asText(status).toLowerCase();
  if (!s) return true;
  if (/^(on|available|active|yes|y)$/i.test(s)) return true;
  return !/^(off|pto|unavailable|out|inactive|n|no)$/i.test(s);
};

const normalizeName = (v) => asText(v).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const namesMatch = (a, b) => {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
};

const cellAt = (sheet, r, c) => {
  const row = (sheet.rows || [])[r];
  if (!row) return '';
  const cell = (row.cells || [])[c];
  if (!cell) return '';
  if (cell.value !== undefined && cell.value !== null && cell.value !== '') return cell.value;
  if (cell.value2 !== undefined && cell.value2 !== null) return cell.value2;
  return '';
};

const sheetSpan = (sheet) => {
  const ur = sheet.usedRange || {};
  const rowCount = Math.max((ur.rowIndex ?? -1) + 1, (sheet.rows || []).length);
  const colCount = Math.max((ur.colIndex ?? -1) + 1, 40);
  return { rowCount, colCount };
};

const findSheet = (sheets, names) => {
  const list = sheets || [];
  for (let i = 0; i < names.length; i += 1) {
    const want = names[i].toLowerCase();
    const hit = list.find((s) => asText(s.name).toLowerCase() === want);
    if (hit) return hit;
  }
  return null;
};

const headerIndex = (sheet, headerRow = 0) => {
  const { colCount } = sheetSpan(sheet);
  const map = {};
  for (let c = 0; c < colCount; c += 1) {
    const key = asText(cellAt(sheet, headerRow, c)).toLowerCase();
    if (key) map[key] = c;
  }
  return map;
};

const colOf = (map, aliases) => {
  for (let i = 0; i < aliases.length; i += 1) {
    if (map[aliases[i]] !== undefined) return map[aliases[i]];
  }
  const keys = Object.keys(map);
  for (let i = 0; i < aliases.length; i += 1) {
    const alias = aliases[i];
    const hit = keys.find((k) => k === alias || k.includes(alias) || alias.includes(k));
    if (hit) return map[hit];
  }
  return -1;
};

const classifyReq = (label) => {
  const t = asText(label).toLowerCase().replace(/\s+/g, '');
  if (t.includes('personnel') || t.includes('people')) return 'personnel';
  if (t.includes('hydro') || t === 'hv') return 'hydro';
  if (t.includes('truck')) return 'trucks';
  if (t.includes('600')) return 'hp600';
  if (t.includes('10x')) return 'pumps';
  if (t.includes('10') && t.includes('"')) return 'hose10';
  if (t.includes('12') && t.includes('"')) return 'hose12';
  if (t === '10"' || t.startsWith('10"')) return 'hose10';
  if (t === '12"' || t.startsWith('12"')) return 'hose12';
  return '';
};

const parseYearMonth = (sheet, sheets) => {
  const name = asText(sheet.name);
  const parts = name.replace(/[^a-zA-Z0-9]/g, ' ').split(/\s+/).filter(Boolean);
  let month = -1;
  let year = 0;
  parts.forEach((p) => {
    const m = MONTHS[p.toLowerCase()];
    if (m !== undefined) month = m;
    if (/^\d{4}$/.test(p)) year = parseInt(p, 10);
    if (/^\d{2}$/.test(p)) {
      const n = parseInt(p, 10);
      year = n >= 70 ? 1900 + n : 2000 + n;
    }
  });
  const { rowCount, colCount } = sheetSpan(sheet);
  for (let r = 0; r < Math.min(rowCount, 16) && month < 0; r += 1) {
    for (let c = 0; c < Math.min(colCount, 4); c += 1) {
      const m = MONTHS[asText(cellAt(sheet, r, c)).toLowerCase()];
      if (m !== undefined) month = m;
    }
  }
  if (!year && sheets) {
    sheets.forEach((s) => {
      if (year) return;
      const parsed = parseYearMonth(s);
      if (parsed.month === month && parsed.year) year = parsed.year;
    });
  }
  if (!year) year = new Date().getFullYear();
  if (month < 0) month = new Date().getMonth();
  return { month, year };
};

const findJobNameRow = (sheet) => {
  const { rowCount, colCount } = sheetSpan(sheet);
  for (let r = 0; r < Math.min(rowCount, 30); r += 1) {
    for (let c = 0; c < Math.min(colCount, 4); c += 1) {
      if (normalizeName(cellAt(sheet, r, c)) === 'job name') return r;
    }
  }
  return -1;
};

const parseCalendarSheet = (sheet) => {
  const headerRow = findJobNameRow(sheet);
  if (headerRow < 0) return null;
  const { rowCount, colCount } = sheetSpan(sheet);
  const days = [];
  for (let c = 0; c < colCount; c += 1) {
    const day = asNumber(cellAt(sheet, headerRow, c));
    if (day >= 1 && day <= 31) {
      const weekday = asText(cellAt(sheet, headerRow - 1, c));
      days.push({ day, col: c, weekday });
    }
  }
  if (!days.length) return null;
  const jobs = [];
  for (let r = headerRow + 1; r < rowCount; r += 1) {
    const name = asText(cellAt(sheet, r, 0));
    if (name && !/equipment totals|job name/i.test(name)) {
      const reqs = {
        hose10: {},
        hose12: {},
        hp600: {},
        pumps: {},
        trucks: {},
        personnel: {},
        hydro: {},
      };
      for (let i = 0; i < REQ_ROWS && (r + i) < rowCount; i += 1) {
        const kind = classifyReq(cellAt(sheet, r + i, 1));
        const reqRow = r + i;
        if (kind) {
          days.forEach((d) => {
            const n = asNumber(cellAt(sheet, reqRow, d.col));
            if (n) reqs[kind][d.day] = n;
          });
        }
      }
      jobs.push({ name, reqs });
      r += REQ_ROWS - 1;
    }
  }
  return { headerRow, days, jobs };
};

const listCalendarSheets = (sheets) => {
  const list = [];
  (sheets || []).forEach((s) => {
    const name = asText(s.name);
    if (!name || /^(job board|associates|equipment|jobs|jobs data)$/i.test(name)) {
      return;
    }
    const calendar = parseCalendarSheet(s);
    if (calendar) {
      list.push({
        name,
        calendar,
        ym: parseYearMonth(s, sheets),
      });
    }
  });
  return list;
};

const daysFromCalendar = (calendar, ym) => (calendar.days || []).map((d) => {
  const date = new Date(ym.year, ym.month, d.day);
  const weekday = d.weekday || WEEKDAYS[date.getDay()];
  const label = `${weekday} ${date.toLocaleString('en-US', { month: 'short' })} ${d.day} ${ym.year}`;
  return {
    key: `${ym.year}-${String(ym.month + 1).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`,
    day: d.day,
    label,
  };
});

const jobsForDay = (calendar, day) => {
  if (!calendar) return [];
  return calendar.jobs.map((job) => {
    const req = {
      hose10: job.reqs.hose10[day] || 0,
      hose12: job.reqs.hose12[day] || 0,
      hp600: job.reqs.hp600[day] || 0,
      pumps: job.reqs.pumps[day] || 0,
      trucks: job.reqs.trucks[day] || 0,
      personnel: job.reqs.personnel[day] || 0,
      hydro: (job.reqs.hydro && job.reqs.hydro[day]) || 0,
    };
    const scheduled = Object.values(req).some((n) => n > 0);
    return scheduled ? { name: job.name, ...req } : null;
  }).filter(Boolean);
};

const parseTableSheet = (sheet, fieldMap) => {
  if (!sheet) return [];
  const map = headerIndex(sheet, 0);
  if (!Object.keys(map).length) return [];
  const { rowCount } = sheetSpan(sheet);
  const rows = [];
  for (let r = 1; r < rowCount; r += 1) {
    const item = {};
    let any = false;
    Object.keys(fieldMap).forEach((out) => {
      const c = colOf(map, fieldMap[out]);
      item[out] = c >= 0 ? cellAt(sheet, r, c) : '';
      if (asText(item[out])) any = true;
    });
    if (any) rows.push(item);
  }
  return rows;
};

const parseAssociatesSheet = (sheet) => parseTableSheet(sheet, {
  name: ['name'],
  email: ['email'],
  mobile: ['mobile', 'phone'],
  dl: ['dl'],
  cdl: ['cdl'],
  manager: ['manager'],
  supervisor: ['supervisor'],
  status: ['status', 'duty'],
  crew: ['crew'],
  roles: ['role', 'roles', 'jobtask', 'job task'],
  department: ['department', 'dept', 'group', 'type', 'category', 'corporate'],
});

const parseEquipmentSheet = (sheet) => parseTableSheet(sheet, {
  truck: ['work truck', 'truck', 'worktruck'],
  hydro: ['hydro vac', 'hydrovac', 'hv'],
  status: ['status'],
  hvStatus: ['hvstatus', 'hv status'],
});

const parseJobsReqSheet = (sheet) => parseTableSheet(sheet, {
  title: ['title', 'job', 'job name', 'name'],
  comments: ['comments', 'description'],
  startTime: ['start time'],
  endTime: ['end time'],
  shifts: ['shifts'],
  cdlReq: ['cdl req', 'cdl'],
  dlReq: ['dl req', 'dl'],
  needed: ['# needed', 'needed', 'personnel'],
  managerReq: ['manager req', 'manager'],
  supervisorReq: ['supervisor req', 'supervisor'],
  crew: ['crew', 'crew pref', 'preferred crew'],
  hydroReq: ['hydro vac req', 'hydro vac', 'hydrovac', 'hv req', 'hv'],
});

const matchJobReq = (name, jobRows) => jobRows.find((j) => namesMatch(name, j.title)) || null;

const roleText = (a) => `${asText(a.roles)} ${asText(a.jobtask)} ${flagYes(a.manager) ? 'manager' : ''} ${flagYes(a.supervisor) ? 'supervisor' : ''}`.toLowerCase();

const isManagerRole = (a) => flagYes(a.manager) || roleText(a).includes('manager');

const isCorporate = (a) => {
  const blob = [
    a.crew,
    a.roles,
    a.jobtask,
    a.department,
    a.group,
    a.type,
    a.category,
    a.corporate,
  ].map((v) => asText(v).toLowerCase()).join(' ');
  return /\bcorporate\b/.test(blob);
};

const pickAssociate = (pool, used, pred, consume = true) => {
  for (let i = 0; i < pool.length; i += 1) {
    const a = pool[i];
    const key = normalizeName(a.name);
    const free = key && (!consume || !used.has(key));
    const ok = !pred || pred(a);
    if (free && ok) {
      if (consume) used.add(key);
      return a;
    }
  }
  return null;
};

const preferredCrewId = (job, available) => {
  const hinted = asText(job.crewPref);
  if (hinted) return hinted;
  const counts = {};
  available.forEach((a) => {
    const crew = asText(a.crew);
    if (!crew) return;
    counts[crew] = (counts[crew] || 0) + 1;
  });
  const needed = job.personnel || 0;
  let best = '';
  let bestScore = -1;
  Object.keys(counts).forEach((crew) => {
    const n = counts[crew];
    let score = n;
    if (needed && n >= needed) {
      score = 100 + needed - (n - needed);
    }
    if (score > bestScore) {
      best = crew;
      bestScore = score;
    }
  });
  return best;
};

const assignJobPeople = (job, associates, used) => {
  const onDuty = associates.filter((a) => a.name && isOnDuty(a.status) && !isCorporate(a));
  const crewId = preferredCrewId(job, onDuty.filter((a) => !used.has(normalizeName(a.name))));
  const ordered = [...onDuty].sort((a, b) => {
    const ac = asText(a.crew) && asText(a.crew) === String(crewId) ? 0 : 1;
    const bc = asText(b.crew) && asText(b.crew) === String(crewId) ? 0 : 1;
    return ac - bc;
  });
  const issues = [];
  const manager = pickAssociate(ordered, used, isManagerRole, false);
  if (!manager) issues.push('Missing manager');
  const supervisor = job.supervisorReq
    ? pickAssociate(ordered, used, (a) => !isManagerRole(a) && (flagYes(a.supervisor) || roleText(a).includes('supervisor')))
    : null;
  if (job.supervisorReq && !supervisor) issues.push('Missing supervisor');
  const drivers = [];
  const driverNeed = job.dlReq ? Math.max(job.trucks || 0, 1) : 0;
  for (let i = 0; i < driverNeed; i += 1) {
    const d = pickAssociate(ordered, used, (a) => !isManagerRole(a) && flagYes(a.dl));
    if (d) drivers.push(d);
  }
  if (driverNeed && drivers.length < driverNeed) issues.push('Missing DL driver');
  const cdlHolders = [];
  const cdlNeed = job.cdlReq ? 1 : 0;
  for (let i = 0; i < cdlNeed; i += 1) {
    const d = pickAssociate(ordered, used, (a) => !isManagerRole(a) && flagYes(a.cdl));
    if (d) cdlHolders.push(d);
  }
  if (cdlNeed && !cdlHolders.length) issues.push('Missing CDL');
  const crew = [];
  const already = [supervisor, ...drivers, ...cdlHolders].filter(Boolean);
  already.forEach((a) => {
    if (isManagerRole(a)) return;
    if (!crew.find((x) => normalizeName(x.name) === normalizeName(a.name))) crew.push(a);
  });
  const need = Math.max(job.personnel || 0, crew.length);
  while (crew.length < need) {
    const next = pickAssociate(ordered, used, (a) => !isManagerRole(a));
    if (!next) break;
    crew.push(next);
  }
  if (crew.length < (job.personnel || 0)) issues.push(`Need ${job.personnel} personnel, assigned ${crew.length}`);
  return { manager, supervisor, drivers, cdlHolders, crew, crewId, issues };
};

const takeEquipment = (pool, used, pred) => {
  for (let i = 0; i < pool.length; i += 1) {
    const item = pool[i];
    const { key } = item;
    const free = !used.has(key);
    const ok = !pred || pred(item);
    if (free && ok) {
      used.add(key);
      return item.label;
    }
  }
  return '';
};

const assignJobEquipment = (job, equipment, used) => {
  const issues = [];
  const trucks = [];
  const truckPool = equipment.filter((e) => e.kind === 'truck');
  const hvPool = equipment.filter((e) => e.kind === 'hydro');
  const pumpPool = equipment.filter((e) => e.kind === 'pump');
  const hpPool = equipment.filter((e) => e.kind === 'hp600');
  for (let i = 0; i < (job.trucks || 0); i += 1) {
    const t = takeEquipment(truckPool, used, (e) => e.available);
    if (t) trucks.push(t);
  }
  if ((job.trucks || 0) && trucks.length < job.trucks) issues.push('Missing work trucks');
  const pumps = [];
  for (let i = 0; i < (job.pumps || 0); i += 1) {
    const p = takeEquipment(pumpPool, used, (e) => e.available);
    pumps.push(p || `10x8-${i + 1}`);
  }
  const hp600 = [];
  for (let i = 0; i < (job.hp600 || 0); i += 1) {
    const p = takeEquipment(hpPool, used, (e) => e.available);
    hp600.push(p || `600hp-${i + 1}`);
  }
  let hydro = '';
  if (job.hydroReq) {
    hydro = takeEquipment(hvPool, used, (e) => e.available);
    if (!hydro) issues.push('Missing hydro vac');
  }
  const hose = [];
  if (job.hose10) hose.push(`10" x ${job.hose10}`);
  if (job.hose12) hose.push(`12" x ${job.hose12}`);
  return { trucks, pumps, hp600, hydro, hose, issues };
};

const enrichJob = (job, jobRows) => {
  const rec = matchJobReq(job.name, jobRows);
  const personnel = job.personnel || asNumber(rec && rec.needed);
  let trucks = job.trucks || 0;
  if (personnel > 0 && personnel <= 4) {
    trucks = 1;
  } else if (personnel > 4 && !trucks) {
    trucks = Math.ceil(personnel / 4);
  }
  const cdlReq = rec ? flagYes(rec.cdlReq) : false;
  const hydroReq = (rec && flagYes(rec.hydroReq)) || asNumber(job.hydro) > 0;
  const dlReq = rec ? flagYes(rec.dlReq) : trucks > 0;
  const managerReq = rec ? flagYes(rec.managerReq) : personnel >= 6;
  const supervisorReq = rec ? flagYes(rec.supervisorReq) : personnel >= 2;
  const shifts = rec ? Math.max(1, asNumber(rec.shifts) || 1) : 1;
  const comments = [asText(rec && rec.comments), job.hose10 ? `10": ${job.hose10}` : '', job.hose12 ? `12": ${job.hose12}` : '', job.hp600 ? `600hp: ${job.hp600}` : '', job.pumps ? `10x8: ${job.pumps}` : '', trucks ? `Trucks: ${trucks}` : '', personnel ? `Personnel: ${personnel}` : '', hydroReq ? 'Hydro Vac: Y' : ''].filter(Boolean).join(' | ');
  return {
    ...job,
    personnel,
    trucks,
    cdlReq,
    hydroReq,
    dlReq,
    managerReq,
    supervisorReq,
    shifts,
    startTime: rec ? asText(rec.startTime) : '',
    endTime: rec ? asText(rec.endTime) : '',
    comments,
    crewPref: rec ? asText(rec.crew) : '',
  };
};

const buildEquipmentPool = (rows, dayJobs) => {
  const pool = [];
  rows.forEach((e, i) => {
    if (asText(e.truck)) {
      pool.push({
        key: `truck-${asText(e.truck)}-${i}`,
        kind: 'truck',
        label: asText(e.truck),
        available: !asText(e.status) || isOnDuty(e.status) || /available/i.test(asText(e.status)),
      });
    }
    if (asText(e.hydro)) {
      pool.push({
        key: `hv-${asText(e.hydro)}-${i}`,
        kind: 'hydro',
        label: asText(e.hydro),
        available: !asText(e.hvStatus) || isOnDuty(e.hvStatus) || /available/i.test(asText(e.hvStatus)),
      });
    }
  });
  if (!pool.find((p) => p.kind === 'truck')) {
    const totalTrucks = dayJobs.reduce((n, j) => n + (j.trucks || 0), 0);
    for (let i = 0; i < Math.max(totalTrucks, 8); i += 1) {
      pool.push({ key: `truck-wt-${i + 1}`, kind: 'truck', label: `WT-${String(i + 100).slice(-3)}`, available: true });
    }
  }
  return pool;
};

const peopleLine = (list) => list.filter(Boolean).map((a) => a.name).join('\n');

const isCoordinatorRole = (a) => roleText(a).includes('coordinator');

const collectAssignedPeople = (assignments) => {
  const byName = {};
  const add = (person, role) => {
    if (!person || !asText(person.name)) return;
    const key = normalizeName(person.name);
    if (!byName[key]) {
      byName[key] = {
        name: person.name,
        email: asText(person.email),
        mobile: asText(person.mobile),
        roles: [],
      };
    }
    if (role && !byName[key].roles.includes(role)) {
      byName[key].roles.push(role);
    }
  };
  (assignments || []).forEach((asg) => {
    const people = asg.people || {};
    add(people.manager, 'manager');
    add(people.supervisor, 'supervisor');
    add(people.coordinator, 'coordinator');
    (people.crew || []).forEach((p) => {
      let role = 'crew';
      if (isCoordinatorRole(p)) role = 'coordinator';
      else if (flagYes(p.supervisor) || roleText(p).includes('supervisor')) role = 'supervisor';
      add(p, role);
    });
  });
  return Object.keys(byName).map((k) => byName[k]);
};

const safeFilePart = (value) => asText(value).replace(/[^\w.-]+/g, '_') || 'org';

const OPEN_MIME = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
  xlsb: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
};

const parseJsonPayload = (value) => {
  let data = value;
  for (let i = 0; i < 2; i += 1) {
    if (typeof data !== 'string') break;
    try {
      data = JSON.parse(data);
    } catch (err) {
      return null;
    }
  }
  return data && typeof data === 'object' ? data : null;
};

const workbookFromOpenJson = (value) => {
  const data = parseJsonPayload(value);
  if (!data) return null;
  const nested = data.jsonObject && (data.jsonObject.Workbook || data.jsonObject.workbook);
  const wrapped = data.data && (data.data.Workbook || data.data.workbook || data.data);
  const workbook = data.Workbook || data.workbook || nested || wrapped
    || (Array.isArray(data.sheets) ? data : null);
  const parsed = parseJsonPayload(workbook) || workbook;
  if (!parsed || !Array.isArray(parsed.sheets)) return null;
  return { Workbook: parsed };
};

const hideOpenSpinner = (spreadsheet) => {
  if (spreadsheet && typeof spreadsheet.hideSpinner === 'function') {
    spreadsheet.hideSpinner();
  }
};

const Spreadsheet = ({ dt }) => {
  const spreadsheetRef = useRef(null);
  // 1. Define your dropdown data as a state variable
  // const [options, setOptions] = useState([]);

  let usr = localStorage.getItem('user');
  let selectedOrg = localStorage.getItem('organization');
  if (!usr || !selectedOrg) {
    // Redirect
    return <p>Please login... </p>;
  }
  usr = JSON.parse(usr);
  selectedOrg = JSON.parse(selectedOrg);
  const { REACT_APP_HTTP_API_ENDPOINT, REACT_APP_API_ENDPOINT } = process.env;
  const [jobsdata, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [schedules, setSchedules] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sheetOptions, setSheetOptions] = useState([]);
  const [selectedSheetName, setSelectedSheetName] = useState('');
  const [dayOptions, setDayOptions] = useState([]);
  const [selectedDayKey, setSelectedDayKey] = useState('');
  const [boardMessage, setBoardMessage] = useState('');
  const fileAssociatesRef = useRef([]);
  const fileEquipmentRef = useRef([]);
  const fileJobsRef = useRef([]);
  const calendarMetaRef = useRef(null);
  const calendarSheetsRef = useRef([]);
  const lastBoardRef = useRef(null);

  const [now, setNow] = useState(new Date());

  const cellSave = (args) => {
    const spreadsheet = spreadsheetRef.current;

    // Perform actions when cell data changes
    // console.log('Cell changed:', args);
    // Example: Update the same cell or another cell to force a re-render
    // spreadsheet.updateCell({ value: args.value }, args.address);
    if (args.address === 'Schedule!A6') {
      spreadsheet.updateCell({ value: args.value }, args.address);
      setNow(new Date(args.value));
    }
  };
  const writeJobBoard = (dayInfo, assignments) => {
    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) return;
    const labels = [
      'Field Mgr in Charge',
      'Job Name',
      'Comments',
      'Crew / Start',
      'Equipment',
      'DAYS',
      'Day Crew',
      '',
      'NIGHTS',
      'Night Crew',
      'Status',
    ];
    const colCount = Math.max(assignments.length + 1, 2);
    const columns = [{ width: 160 }];
    for (let i = 1; i < colCount; i += 1) {
      columns.push({ width: 220 });
    }
    const jobValues = assignments.map((asg) => {
      const nightCrew = asg.job.shifts > 1 ? asg.people.crew.slice(Math.ceil(asg.people.crew.length / 2)) : [];
      const dayCrew = asg.job.shifts > 1 ? asg.people.crew.slice(0, Math.ceil(asg.people.crew.length / 2)) : asg.people.crew;
      const equip = [
        asg.equip.trucks.length ? `Trucks: ${asg.equip.trucks.join(', ')}` : '',
        asg.equip.hp600.length ? `600hp: ${asg.equip.hp600.join(', ')}` : '',
        asg.equip.pumps.length ? `10x8: ${asg.equip.pumps.join(', ')}` : '',
        asg.equip.hydro ? `Hydro Vac: ${asg.equip.hydro}` : '',
        asg.equip.hose.length ? asg.equip.hose.join(', ') : '',
      ].filter(Boolean).join('\n');
      const issues = [...asg.people.issues, ...asg.equip.issues];
      return [
        asg.people.manager ? asg.people.manager.name : 'Unassigned',
        asg.job.name,
        asg.job.comments,
        `Crew ${asg.people.crewId || 'open'}${asg.job.startTime ? ` | Start: ${asg.job.startTime}` : ''}`,
        equip || 'None',
        'DAYS',
        peopleLine(dayCrew) || 'Unassigned',
        '',
        asg.job.shifts > 1 ? 'NIGHTS' : '',
        peopleLine(nightCrew),
        issues.length ? `ISSUES: ${issues.join(', ')}` : 'OK',
      ];
    });
    const rows = [
      {
        cells: [{
          value: `${dt.title}\n\n${dayInfo.label}\n\nJob Board`,
          colSpan: colCount,
          rowSpan: 5,
          style: { fontWeight: 'bold', textAlign: 'center', verticalAlign: 'middle' },
        }],
      },
      {},
      {},
      {},
      {},
      {},
    ];
    labels.forEach((label, rowIdx) => {
      const cells = [{ value: label, style: { fontWeight: 'bold' } }];
      jobValues.forEach((vals) => {
        cells.push({ value: vals[rowIdx] });
      });
      rows.push({ cells });
    });
    const model = { name: 'Job Board', rows, columns };
    const existingIdx = (spreadsheet.sheets || []).findIndex((s) => asText(s.name).toLowerCase() === 'job board');
    const stayOn = spreadsheet.activeSheetIndex;
    if (existingIdx >= 0) {
      if (stayOn === existingIdx && stayOn > 0) {
        spreadsheet.activeSheetIndex = 0;
      }
      try {
        spreadsheet.delete(existingIdx);
      } catch (err) {
        spreadsheet.delete('Job Board');
      }
    }
    window.setTimeout(() => {
      const ss = spreadsheetRef.current;
      if (!ss) return;
      const current = ss.activeSheetIndex;
      ss.insertSheet([model], current);
      window.setTimeout(() => {
        const next = spreadsheetRef.current;
        if (!next) return;
        const idx = (next.sheets || []).findIndex((s) => asText(s.name).toLowerCase() === 'job board');
        if (idx >= 0) {
          next.activeSheetIndex = idx;
        }
      }, 200);
    }, existingIdx >= 0 ? 200 : 0);
  };

  const createJobBoard = () => {
    const spreadsheet = spreadsheetRef.current;
    const meta = calendarMetaRef.current;
    if (!spreadsheet || !meta) {
      setBoardMessage('Open a Monthly Ops Calendar file first.');
      return;
    }
    const dayInfo = dayOptions.find((d) => d.key === selectedDayKey);
    if (!dayInfo) {
      setBoardMessage('Pick a sheet and a day.');
      return;
    }
    const rawJobs = jobsForDay(meta.calendar, dayInfo.day);
    if (!rawJobs.length) {
      const msg = `No active jobs on ${dayInfo.label}.`;
      setBoardMessage(msg);
      DialogUtility.alert(msg);
      return;
    }
    const jobRows = fileJobsRef.current.length ? fileJobsRef.current : [];
    const dayJobs = rawJobs.map((j) => enrichJob(j, jobRows));
    const peopleSource = fileAssociatesRef.current.length ? fileAssociatesRef.current : associates;
    const equipPool = buildEquipmentPool(fileEquipmentRef.current, dayJobs);
    const usedPeople = new Set();
    const usedEquip = new Set();
    const assignments = dayJobs.map((job) => ({
      job,
      people: assignJobPeople(job, peopleSource, usedPeople),
      equip: assignJobEquipment(job, equipPool, usedEquip),
    }));
    lastBoardRef.current = { dayInfo, assignments };
    writeJobBoard(dayInfo, assignments);
    const issues = assignments.filter((a) => a.people.issues.length || a.equip.issues.length).length;
    setBoardMessage(`Job Board created for ${dayInfo.label}: ${assignments.length} jobs${issues ? `, ${issues} with issues` : ''}.`);
  };

  const getJobBoardWorkbook = async (spreadsheet) => {
    const json = await spreadsheet.saveAsJson();
    const workbook = (json && json.jsonObject && json.jsonObject.Workbook) || json;
    const sheets = (workbook && workbook.sheets) || [];
    const jobBoardSheets = sheets.filter((s) => asText(s.name).toLowerCase() === 'job board');
    if (!jobBoardSheets.length) {
      throw new Error('Job Board sheet not found');
    }
    return { ...workbook, sheets: jobBoardSheets };
  };

  const sendJobBoard = async (type) => {
    const spreadsheet = spreadsheetRef.current;
    const board = lastBoardRef.current;
    if (!spreadsheet || !board) {
      DialogUtility.alert('Create a Job Board first.');
      return;
    }
    const hasSheet = (spreadsheet.sheets || []).some((s) => asText(s.name).toLowerCase() === 'job board');
    if (!hasSheet) {
      DialogUtility.alert('Job Board sheet not found. Create a Job Board first.');
      return;
    }
    const assigned = collectAssignedPeople(board.assignments);
    const orgPart = safeFilePart(dt.title || selectedOrg.title);
    const datePart = board.dayInfo.key || selectedDayKey || 'undated';
    const filename = `JobBoard_${orgPart}_${datePart}.xlsx`;
    try {
      const workbookToSave = await getJobBoardWorkbook(spreadsheet);
      const jsonText = JSON.stringify(workbookToSave);
      const jsonFile = new File(
        [jsonText],
        filename.replace(/\.xlsx$/i, '.json'),
        { type: 'application/json' },
      );
      const payload = new FormData();
      payload.append('file', jsonFile, jsonFile.name);
      payload.append('JSONData', jsonText);
      payload.append('filename', filename);
      payload.append('saveType', 'Xlsx');
      payload.append('associates', JSON.stringify(assigned));
      payload.append('organization', dt.title || '');
      payload.append('organizationid', dt._id || selectedOrg.id || '');
      payload.append('date', datePart);
      payload.append('type', type || 'email');
      const query = Object.entries(usr)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
      const url = `${REACT_APP_API_ENDPOINT}/excel?${query}&jobboard=true`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `JWTToken ${usr.token}`,
        },
        body: payload,
      });
      if (!response.ok) {
        DialogUtility.alert('Job Board SMS request failed.');
        return;
      }
      DialogUtility.alert('Job Board SMS request sent.');
    } catch (err) {
      DialogUtility.alert('Could not send Job Board SMS.');
    }
  };

  const fileMenuItemSelect = (args) => {
    // Check if the clicked item is your custom item
    if (args.item.text === 'EMAIL') {
      // Add your custom logic here
      sendJobBoard(args.item.text);
    }
    if (args.item.text === 'SMS') {
      sendJobBoard(args.item.text);
    }
    if (args.item.text === 'JOB BOARD') {
      createJobBoard();
    }
  };
  /*
  const onCreated = () => {
    const spreadsheet = spreadsheetRef.current;
    spreadsheet.updateRange({
      dataSource: schedules,
      startCell: 'A7',
      width: 200,
    });
    if (spreadsheet.activeSheetIndex === 0) {
      spreadsheet.setColWidth(200, 0);
      spreadsheet.setColWidth(200, 1);
      spreadsheet.setColWidth(200, 2);
      spreadsheet.setColWidth(200, 3);
      spreadsheet.setColWidth(200, 4);
      spreadsheet.setColWidth(200, 5);
      spreadsheet.setColWidth(200, 6);
      spreadsheet.setColWidth(200, 7);
      spreadsheet.setColWidth(200, 8);
      spreadsheet.setColWidth(200, 9);
      spreadsheet.setColWidth(200, 10);
      spreadsheet.setColWidth(200, 11);
      spreadsheet.setColWidth(200, 12);
      spreadsheet.setColWidth(200, 13);
      spreadsheet.setColWidth(200, 14);
      spreadsheet.setColWidth(200, 15);
      spreadsheet.cellFormat({ textAlign: 'center' }, 'A7:Z200');
      spreadsheet.wrap('A7:Z200');
      spreadsheet.hideRow(6, 6, true);
      spreadsheet.cellFormat({ fontWeight: 'bold' }, 'A7:Z7');
      spreadsheet.cellFormat({ fontWeight: 'bold', color: 'red' }, 'A14:Z14');
    }

    //spreadsheet.updateRange({
     // dataSource: associates,
   //   sheetIndex: 1,
      startCell: 'A1',
    //});
    //spreadsheet.updateRange({
    //  dataSource: jobsdata,
    //  sheetIndex: 2,
    //  startCell: 'A1',
    // });

    if (spreadsheetRef.current && spreadsheet.activeSheetIndex === 0) {
      // 2. Format options as a comma-separated string for the 'List' type
      const listSource = options.join(',');
      // 3. Assign validation to the specific column range (e.g., B2:B10)
      spreadsheetRef.current.addDataValidation({
        type: 'List',
        value1: listSource,
        ignoreBlank: true,
        inCellDropDown: true,
      }, 'C6:C20'); // Target Range
    }
    const emailItem = [{ text: 'EMAIL', id: 'email', iconCss: 'e-icons e-emoji' }];
    const smsItem = [{ text: 'SMS', id: 'sms', iconCss: 'e-icons e-next-comment' }];
    // Add the item to the file menu (placed after 'Print' in this example)
    spreadsheet.addFileMenuItems(emailItem, 'Print', false);
    spreadsheet.addFileMenuItems(smsItem, 'EMAIL', false);
  };
*/
  const applyCalendarSheet = (sheetName, list) => {
    const calendars = list || calendarSheetsRef.current;
    const found = calendars.find((s) => s.name === sheetName) || calendars[0];
    if (!found) return;
    const options = daysFromCalendar(found.calendar, found.ym);
    calendarMetaRef.current = { sheetName: found.name, calendar: found.calendar, ym: found.ym };
    setSheetOptions(calendars.map((s) => s.name));
    setSelectedSheetName(found.name);
    setDayOptions(options);
    setSelectedDayKey(options[0] ? options[0].key : '');
    setBoardMessage(`Using ${found.name}. Pick a day and create the Job Board.`);
    const ss = spreadsheetRef.current;
    if (ss) {
      const idx = (ss.sheets || []).findIndex((s) => asText(s.name) === found.name);
      if (idx >= 0 && ss.activeSheetIndex !== idx) {
        ss.activeSheetIndex = idx;
      }
    }
  };

  const openComplete = () => {
    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) return;
    const { sheets } = spreadsheet;
    fileAssociatesRef.current = parseAssociatesSheet(findSheet(sheets, ['Associates']));
    fileEquipmentRef.current = parseEquipmentSheet(findSheet(sheets, ['Equipment']));
    fileJobsRef.current = parseJobsReqSheet(findSheet(sheets, ['Jobs', 'Jobs Data']));
    const calendars = listCalendarSheets(sheets);
    calendarSheetsRef.current = calendars;
    if (!calendars.length) {
      setCalendarOpen(false);
      setSheetOptions([]);
      setDayOptions([]);
      setBoardMessage('');
      return;
    }
    const activeName = asText((sheets[spreadsheet.activeSheetIndex] || {}).name);
    const initial = calendars.find((s) => s.name === activeName) || calendars[0];
    applyCalendarSheet(initial.name, calendars);
    setCalendarOpen(true);
    const emailItem = [{ text: 'EMAIL', id: 'email', iconCss: 'e-icons e-emoji' }];
    const smsItem = [{ text: 'SMS', id: 'sms', iconCss: 'e-icons e-next-comment' }];
    const boardItem = [{ text: 'JOB BOARD', id: 'jobboard', iconCss: 'e-icons e-timeline-week' }];
    spreadsheet.addFileMenuItems(emailItem, 'Print', false);
    spreadsheet.addFileMenuItems(smsItem, 'EMAIL', false);
    spreadsheet.addFileMenuItems(boardItem, 'SMS', false);
  };

  const openFailure = () => {
    hideOpenSpinner(spreadsheetRef.current);
    DialogUtility.alert('Could not open the spreadsheet file.');
  };

  const beforeOpen = (args) => {
    if (!args || args.requestType === 'chunk') return;
    const eventArgs = args;
    const { file } = eventArgs;
    if (!file || typeof file === 'string' || !file.name) return;
    const ext = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
    const mime = OPEN_MIME[ext];
    if (!mime) {
      eventArgs.cancel = true;
      DialogUtility.alert('Unsupported file type. Please open an Excel file (.xlsx, .xls, .xlsm, .xlsb, or .csv).');
      return;
    }
    eventArgs.cancel = true;
    const upload = file.type === mime ? file : new File([file], file.name, { type: mime });
    let body = eventArgs.requestData && eventArgs.requestData.body;
    if (!(body instanceof FormData)) {
      body = new FormData();
    }
    if (typeof body.set === 'function') {
      body.set('file', upload);
    } else {
      body.append('file', upload);
    }
    const spreadsheet = spreadsheetRef.current;
    let loaded = false;
    fetch(`${REACT_APP_HTTP_API_ENDPOINT}/spreadsheet/open`, {
      method: 'POST',
      body,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Open failed (${response.status})`);
        }
        return response.text().then((text) => {
          try {
            return JSON.parse(text);
          } catch (err) {
            throw new Error('Open response is not valid JSON.');
          }
        });
      })
      .then((data) => {
        const payload = workbookFromOpenJson(data);
        if (!payload) {
          throw new Error('Open response is not a spreadsheet workbook.');
        }
        if (!spreadsheetRef.current) {
          throw new Error('Spreadsheet is not ready.');
        }
        loaded = true;
        spreadsheetRef.current.openFromJson({ file: payload });
        window.setTimeout(() => {
          openComplete();
          hideOpenSpinner(spreadsheetRef.current);
        }, 0);
      })
      .catch((err) => {
        DialogUtility.alert(err && err.message ? err.message : 'Could not open the spreadsheet file.');
      })
      .finally(() => {
        if (!loaded) hideOpenSpinner(spreadsheet || spreadsheetRef.current);
      });
  };
  // const [mgrData, setMgrData] = useState([]);
  useEffect(() => {
    // limit associates to the overall selection
    if (usr && selectedOrg && selectedOrg.id && selectedOrg.id.length > 0) {
      usr.organizationid = dt._id;
      usr.organization = dt.title;
    }
    // fetch data
    const dataFetch = async () => {
      try {
        const query = Object.entries(usr)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join('&');
        const url = `${REACT_APP_API_ENDPOINT}/jobs?${query}`;
        const mydata = await (
          await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `JWTToken ${usr.token}`,
            },
          })
        ).json()
          .catch((err) => {
            console.error('Error parsing JSON:', err);
            return null;
          });
        const mylocs = [];
        if (mydata.locations) {
          mydata.locations.forEach((kml) => {
            if (kml.document.name.includes('~') && kml.document.folder && kml.document.folder.name && !kml.document.folder.name.includes('~')) {
              mylocs.push({ value: kml._id, text: kml.document.folder.name });
            } else {
              mylocs.push({ value: kml._id, text: kml.document.name });
            }
          });
        }
        const mymgrs = [];
        if (mydata.managers) {
          mydata.managers.forEach((mgr) => {
            // mymgrs.push({ value: mgr._id, text: `${mgr.first} ${mgr.last}` });
            mymgrs.push(`${mgr.first} ${mgr.last}`);
          });
        }
        const mySchedule = [];
        const myScheduleTitle = [];
        const myScheduleComments = [];
        const myScheduleStart = [];
        const myScheduleEnd = [];
        const myScheduleStartTime = [];
        const myScheduleEndTime = [];
        const myScheduleManager = [];
        const myScheduleDays = [];
        const myScheduleNights = [];
        const myScheduleDaysA = [];
        const myScheduleNightsA = [];
        const myScheduleBlank = [];
        mydata.jobs.forEach((element, index) => {
          /*
          if (!element.title) {
            console.log('no element title');
            // return;
          }
          if (!element.date || !element.enddate) {
            console.log('no element date');
            // return;
          }
          */
          /*
          const sdt = new Date(element.date);
          const edt = new Date(element.enddate);
          if (sdt === 'Invalid Date' || edt === 'Invalid Date') {
            console.log('invalid data');
            // return;
          }

          if (now < sdt) {
            console.log('now < sdt', now, mydata.jobs[index].title);
            // return;
          }
          if (now > edt) {
            console.log('now > edt', now, mydata.jobs[index].title);
            // return;
          }
          */
          if (element.location && element.location.name) {
            mydata.jobs[index].locationName = element.location.name;
          } else {
            mydata.jobs[index].locationName = 'unassigned';
          }
          // const { title } = element;

          let manager = 'Unassigned';
          if (element.manager) {
            manager = element.manager;
          }
          if (!mymgrs.includes(manager)) {
            mymgrs.push(manager);
          }
          /*
          let comments = '';
          if (element.description) {
            comments = element.description;
          }
          */
          // const schedule = { title, comments, manager, start: element.date, time: element.time, end: element.enddate, endtime: element.endtime };
          // mySchedule.push(schedule);
          myScheduleTitle.push(element.title);
          myScheduleComments.push(`Comments: ${element.description}`);
          myScheduleStart.push(element.date);
          myScheduleStartTime.push(`Start Time: ${element.time}`);
          myScheduleEnd.push(element.enddate);
          myScheduleEndTime.push(element.endtime);
          myScheduleManager.push(`Field Mgr in Charge: ${element.manager}`);

          myScheduleDaysA.push(associates[0].name);
          myScheduleDays.push('DAYS');
          myScheduleBlank.push('');

          if (element.time === element.endtime) {
            myScheduleNights.push('NIGHTS');
            myScheduleNightsA.push(associates[1].name);
          } else {
            myScheduleNights.push('');
            myScheduleNightsA.push('');
          }
        });
        // setMgrData(mymgrs);
        // setOptions(mymgrs);
        mySchedule.push(myScheduleManager);
        mySchedule.push(myScheduleTitle);
        mySchedule.push(myScheduleComments);
        // mySchedule.push(myScheduleStart);
        mySchedule.push(myScheduleBlank);
        mySchedule.push(myScheduleStartTime);
        // mySchedule.push(myScheduleEnd);
        mySchedule.push(myScheduleBlank);
        // mySchedule.push(myScheduleEndTime);
        mySchedule.push(myScheduleDays);
        mySchedule.push(myScheduleDaysA);
        mySchedule.push(myScheduleBlank);
        mySchedule.push(myScheduleNights);
        mySchedule.push(myScheduleNightsA);
        // setSchedules(mySchedule);
        setJobsData(mydata.jobs);
        setLoading(false);
        if (spreadsheetRef && spreadsheetRef.current && !calendarMetaRef.current) {
          const spreadsheet = spreadsheetRef.current;
          // spreadsheet.refresh(); // "true" creates a new workbook
          spreadsheet.updateRange({
            dataSource: mySchedule,
            startCell: 'A7',
            width: 200,
          });
          if (spreadsheet.activeSheetIndex === 0) {
            spreadsheet.setColWidth(200, 0);
            spreadsheet.setColWidth(200, 1);
            spreadsheet.setColWidth(200, 2);
          }
        }
      } catch (err) {
        setJobsData([]);
        setLoading(false);
      }
    };
    // set state when the data received
    if (associates.length > 0) {
      dataFetch();
    }
  }, [now, associates]); // mapDisplay
  // const [mgrData, setMgrData] = useState([]);
  useEffect(() => {
    // limit associates to the overall selection
    if (selectedOrg && selectedOrg.id && selectedOrg.id.length > 0) {
      usr.organizationid = dt._id;
      usr.organization = dt.title;
    }
    // fetch data
    const associateFetch = async () => {
      try {
        const query = Object.entries(usr)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join('&');
        const url = `${REACT_APP_API_ENDPOINT}/associates?${query}`;
        const mydata = await (
          await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `JWTToken ${usr.token}`,
            },
          })
        ).json()
          .catch((err) => {
            console.error('Error parsing JSON:', err);
            return null;
          });
        const myAssociates = [];
        mydata.associates.forEach((a) => {
          const e = `=HYPERLINK("mailto:"${a.email}, Email ${a.email})`;
          const ma = {
            name: a.name,
            mobile: a.mobile,
            email: e,
            dl: a.dl || a.DL || 'Y',
            cdl: a.cdl || a.CDL || 'N',
            status: a.status || a.duty || 'Available',
            manager: a.manager || 'N',
            supervisor: a.supervisor || 'N',
            crew: a.crew || '',
            jobtask: a.jobtask,
            roles: a.role || a.jobtask || '',
            department: a.department || a.dept || a.group || '',
          };
          myAssociates.push(ma);
        });
        setAssociates(myAssociates);
      } catch (err) {
        setAssociates([]);
      }
    };
    // set state when the data received
    associateFetch();
  }, []); // mapDisplay
  if (loading) {
    return <p>loading...</p>;
  }
  return (
    <div className="m-2 md:m-1 mt-2 p-1 md:p-4 bg-white rounded-3xl zackat-divalh">
      <Header category="Administration" title="Spreadsheet" />
      {calendarOpen && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label htmlFor="sheet" className="font-semibold flex items-center gap-2">
            Sheet
            <select
              value={selectedSheetName}
              onChange={(e) => applyCalendarSheet(e.target.value)}
              className="border rounded px-2 py-1 font-normal"
            >
              {sheetOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </label>
          <label htmlFor="day" className="font- semibold flex items-center gap-2">
            Day
            <select
              value={selectedDayKey}
              onChange={(e) => setSelectedDayKey(e.target.value)}
              className="border rounded px-2 py-1 font-normal"
            >
              {dayOptions.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="e-btn e-primary e-small zackat-button"
            onClick={createJobBoard}
          >
            Create Job Board
          </button>
          {boardMessage ? <span>{boardMessage}</span> : null}
        </div>
      )}
      <SpreadsheetComponent
        ref={spreadsheetRef}
        // created={onCreated}
        allowOpen
        fileMenuItemSelect={fileMenuItemSelect}
        cellSave={cellSave}
        openComplete={openComplete}
        height={800}
        allowSave
        allowWrap
        // openUrl="https://document.syncfusion.com/web-services/spreadsheet-editor/api/spreadsheet/open"
        // saveUrl="https://document.syncfusion.com/web-services/spreadsheet-editor/api/spreadsheet/save"
        openUrl={`${REACT_APP_HTTP_API_ENDPOINT}/spreadsheet/open`}
        saveUrl={`${REACT_APP_HTTP_API_ENDPOINT}/spreadsheet/save`}
        beforeOpen={beforeOpen}
        openFailure={openFailure}
      >
        <SheetsDirective>
          <SheetDirective name="Schedule" showHeaders={false}>
            <RowsDirective index={0}>
              {/* Define Row 1 as a Header */}
              <RowDirective>
                <CellsDirective>
                  <CellDirective
                    value={`${dt.title}\n\n${now}\n\nWork Schedule\nOpen the Monthly Ops Calendar to begin`}
                    index={0}
                    colSpan={26}
                    rowSpan={5}
                    style={{ textAlign: 'Center', verticalAlign: 'Middle' }}
                  />
                </CellsDirective>
              </RowDirective>
            </RowsDirective>
          </SheetDirective>
          {/* Sheet 1: Mapped from array */}
          <SheetDirective name="Associates">
            <RangesDirective>
              <RangeDirective dataSource={associates} />
            </RangesDirective>
            <ColumnsDirective>
              <ColumnDirective width={150} />
              <ColumnDirective width={100} />
              <ColumnDirective width={200} />
              <ColumnDirective width={40} />
              <ColumnDirective width={40} />
              <ColumnDirective width={40} />
              <ColumnDirective width={40} />
              <ColumnDirective width={200} />
            </ColumnsDirective>
          </SheetDirective>
          {/* Sheet 1: Mapped from array */}
          <SheetDirective name="Jobs Data">
            <RangesDirective>
              <RangeDirective dataSource={jobsdata} />
            </RangesDirective>
            <ColumnsDirective>
              <ColumnDirective width={150} />
              <ColumnDirective width={100} />
              <ColumnDirective width={100} />
            </ColumnsDirective>
          </SheetDirective>
        </SheetsDirective>
      </SpreadsheetComponent>
      <div className="e-adaptive-demo e-bigger" />
    </div>
  );
};
export default Spreadsheet;
